import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../Common/LoadingSpinner';
import toast from 'react-hot-toast';
import { formatDateTime } from '../../utils/dateUtils';

const PaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    filterPayments();
  }, [payments, searchTerm, statusFilter]);

  const fetchPayments = async () => {
    try {
      const response = await axios.get('/api/enrollments/payments');
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const filterPayments = () => {
    let filtered = payments;

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.student?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.student?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.student?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.course?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.razorpayOrderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.razorpayPaymentId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPayments(filtered);
  };

  const getStatusBadge = (status) => {
    const badges = {
      created: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon, label: 'Pending' },
      paid: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon, label: 'Paid' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircleIcon, label: 'Failed' },
      refunded: { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircleIcon, label: 'Refunded' }
    };

    const badge = badges[status] || badges.created;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="h-3 w-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const stats = [
    {
      name: 'Total Payments',
      value: payments.length,
      icon: CreditCardIcon,
      color: 'text-blue-600',
      bg: 'bg-blue-100'
    },
    {
      name: 'Successful',
      value: payments.filter(p => p.status === 'paid').length,
      icon: CheckCircleIcon,
      color: 'text-green-600',
      bg: 'bg-green-100'
    },
    {
      name: 'Pending',
      value: payments.filter(p => p.status === 'created').length,
      icon: ClockIcon,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100'
    },
    {
      name: 'Total Amount (Paid)',
      value: `₹${payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2)}`,
      icon: CreditCardIcon,
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    }
  ];

  const exportPaymentsToCSV = () => {
    if (filteredPayments.length === 0) {
      toast.error('No payments to export');
      return;
    }

    const csvHeaders = [
      'User Name',
      'Role',
      'Phone',
      'Email',
      'Course Name',
      'Amount',
      'Payment Status',
      'Order ID',
      'Payment ID',
      'Date'
    ];

    const csvRows = filteredPayments.map(payment => {
      const statusLabels = {
        created: 'Pending',
        paid: 'Paid',
        failed: 'Failed',
        refunded: 'Refunded'
      };

      return [
        `${payment.student?.firstName || ''} ${payment.student?.lastName || ''}`.trim() || 'N/A',
        payment.student?.role ? payment.student.role.charAt(0).toUpperCase() + payment.student.role.slice(1) : 'N/A',
        payment.student?.phone || 'N/A',
        payment.student?.email || 'N/A',
        payment.course?.title || 'N/A',
        payment.amount || 0,
        statusLabels[payment.status] || payment.status || 'Unknown',
        payment.razorpayOrderId || 'N/A',
        payment.razorpayPaymentId || 'Pending',
        payment.createdAt ? new Date(payment.createdAt).toLocaleDateString('IN') + ' ' + new Date(payment.createdAt).toLocaleTimeString('IN') : 'N/A'
      ];
    });

    const csvContent = [csvHeaders, ...csvRows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `payments_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Payments data exported successfully');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Payment Management</h1>
        <p className="mt-2 text-gray-600">
          Monitor and track all payment transactions
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="card">
              <div className="flex items-center">
                <div className={`${stat.bg} rounded-lg p-3`}>
                  <Icon className={`h-6 w-6 ${stat.color}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by student, course, order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input"
          >
            <option value="all">All Status</option>
            <option value="created">Pending</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </div>

      {/* Payments Table */}
      <div className="card overflow-hidden">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Payments</h2>
          <button
            onClick={exportPaymentsToCSV}
            className="btn btn-primary flex items-center"
          >
            <DocumentIcon className="h-5 w-5 mr-2" />
            Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No payments found
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment) => (
                  <tr key={payment._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {payment.student?.firstName} {payment.student?.lastName}
                        </div>
                        <div className="text-sm text-gray-500">{payment.student?.phone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{payment.course?.title || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{payment.course?.courseCode || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ₹{payment.amount || 0}
                      </div>
                      <div className="text-xs text-gray-500">{payment.currency || 'INR'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-900 font-mono">
                        {payment.razorpayOrderId || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-900 font-mono">
                        {payment.razorpayPaymentId || 'Pending'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(payment.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentManagement;
